import { zonedAddDuration, zonedSetTime, zonedStartOfDay } from '@csp/csp-common-date-util';
import { CspError, CustomStatuses, Maybe, Time, TimeStr, TimeZoneId, ZonedDateTime } from '@csp/csp-common-model';
import { compact, findLast, orderBy } from 'lodash';
import { ActionTimestampWithValidity } from '../model/ActionTimestampWithValidity';
import { ScheduleReferenceDates } from '../../model/ScheduleReferenceDates';
import { ScheduleWindowInterval } from '../../model/ScheduleWindowInterval';
import { Duration } from '../../model/schedulingModels/Duration';
import { FixedAction } from '../../model/schedulingModels/FixedAction';
import { FixedActionType } from '../../model/schedulingModels/FixedActionType';
import { GenericRequest } from '../../model/schedulingModels/GenericRequest';
import { RequestAction } from '../../model/schedulingModels/RequestAction';
import { ScheduleDateService } from '../../service/ScheduleDateService';
import { ScheduleIntervalService } from '../../service/ScheduleIntervalService';
import { SchedulingConst } from '../../type/SchedulingConst';
import { VisitReferenceDate } from '../../model/VisitReferenceDate';

const getStartActionTimestampsFromCustomStatus = ({
  fixedAction,
  customStatuses,
  zone,
  fixedTime,
  offset,
}: {
  fixedAction: FixedAction;
  customStatuses: Maybe<CustomStatuses>;
  zone?: TimeZoneId;
  fixedTime?: TimeStr;
  offset?: Duration;
}): ActionTimestampWithValidity[] => {
  const matchingStatusValues = customStatuses
    ?.getCustomStatusValuesOldestFirst(fixedAction.subType, true)
    .filter(statusValue => statusValue.value === fixedAction.value);

  // Filter out the oldest valid values and all prior invalid values.
  // If no valid then we take all invalid. We do this way to enforce that
  // a status needs to be invalidated for a schedule to be affected.
  const oldestValidValue = matchingStatusValues?.find(statusValue => !statusValue.invalid);
  const historicalInvalidValues = matchingStatusValues?.filter(
    statusValue =>
      (!oldestValidValue ||
        statusValue.createdTimestamp.unixTimeMillis < oldestValidValue.createdTimestamp.unixTimeMillis) &&
      statusValue.invalid,
  );

  const statusesThatHaveAffectedSchedule = historicalInvalidValues?.concat(compact([oldestValidValue]));

  const actionTimestampsWithValidity = statusesThatHaveAffectedSchedule?.reduce<ActionTimestampWithValidity[]>(
    (allActionTimestampWithValidity, customStatusValue, i) => {
      const actionOffsetTimestamp = applyActionOffsetAndFixedTime(
        {
          fixedTime,
          offset,
        },
        customStatusValue.timestamp,
        zone,
      );
      const actionTimestamp = customStatusValue.timestamp;

      if (i === 0) {
        return [
          {
            actionTimestamp,
            validBetween: ScheduleWindowInterval.from(actionOffsetTimestamp, customStatusValue.invalidationDate),
            isValid: !customStatusValue.invalid,
          },
        ] satisfies ActionTimestampWithValidity[];
      } else {
        const allIntervalsWithPrecedence = allActionTimestampWithValidity.map(
          actionTimestampWithValidity => actionTimestampWithValidity.validBetween,
        );
        const currentStatusValidity = ScheduleWindowInterval.from(
          actionOffsetTimestamp,
          customStatusValue.invalidationDate,
        );
        const diffs = ScheduleIntervalService.differenceOfIntervalLists(
          [currentStatusValidity],
          allIntervalsWithPrecedence,
        );

        const newActionTimestampWithValidity: ActionTimestampWithValidity[] = diffs.map(diff => ({
          actionTimestamp,
          isValid: !customStatusValue.invalid,
          validBetween: diff,
        }));

        return [...allActionTimestampWithValidity, ...newActionTimestampWithValidity];
      }
    },
    [],
  );

  return orderBy(
    actionTimestampsWithValidity ?? [],
    actionTimestampWithValidity => actionTimestampWithValidity.validBetween.windowStart.unixTimeMillis,
    'asc',
  );
};

const getStartActionTimstampsFromVisits = ({
  fixedAction,
  appointmentReferenceDates,
  zone,
  fixedTime,
  offset,
}: {
  fixedAction: FixedAction;
  appointmentReferenceDates: Maybe<VisitReferenceDate[]>;
  zone?: TimeZoneId;
  fixedTime?: TimeStr;
  offset?: Duration;
}): [ActionTimestampWithValidity] | [] => {
  const matchingVisits = (appointmentReferenceDates ?? []).filter(
    ({ requestRef }) => requestRef.scheduleCode === fixedAction.subType && requestRef.requestCode === fixedAction.value,
  );

  const oldestMatchingVisit = orderBy(matchingVisits, ({ startTimestamp }) => startTimestamp.unixTimeMillis, 'asc')[0];

  if (oldestMatchingVisit) {
    const actionOffsetTimestamp = applyActionOffsetAndFixedTime(
      {
        fixedTime,
        offset,
      },
      oldestMatchingVisit.startTimestamp,
      zone,
    );
    return [
      {
        actionTimestamp: oldestMatchingVisit.startTimestamp,
        validBetween: ScheduleWindowInterval.from(actionOffsetTimestamp, undefined),
        isValid: true,
        actionSourceId: oldestMatchingVisit.visitId,
      },
    ];
  } else {
    return [];
  }
};

const timeOverrideIfApplicable = (datetime: ZonedDateTime, timeOverride?: TimeStr): ZonedDateTime => {
  // If no time override is specified default to 00:00 on current date
  if (!timeOverride) {
    return zonedStartOfDay(datetime);
  } else {
    const time = Time.from(timeOverride);
    return zonedSetTime(datetime, time.hours, time.minutes);
  }
};

const applyActionFixedTime = (requestAction: Maybe<RequestAction>, timestamp: ZonedDateTime): ZonedDateTime =>
  timeOverrideIfApplicable(timestamp, requestAction?.fixedTime);

const applyActionOffset = (requestAction: Maybe<RequestAction>, timestamp: ZonedDateTime): ZonedDateTime => {
  const actionOffset: Duration = requestAction?.offset ?? SchedulingConst.DEFAULT_ACTION_OFFSET;

  // Tsa    Tsa+offset
  // |---------|----------------------------------------------------------->
  return zonedAddDuration(timestamp, ScheduleDateService.toDateTimeDuration(actionOffset));
};

const applyActionOffsetAndFixedTime = (
  requestAction: Maybe<RequestAction>,
  timestamp: ZonedDateTime,
  timezoneId?: TimeZoneId,
): ZonedDateTime => {
  const startActionTimestampWithFixedTime = applyActionFixedTime(requestAction, timestamp);
  const startActionTimestampWithFixedTimeInTz = timezoneId
    ? ScheduleDateService.replaceTimezoneNameInZonedDate(startActionTimestampWithFixedTime, timezoneId)
    : startActionTimestampWithFixedTime;
  return applyActionOffset(requestAction, startActionTimestampWithFixedTimeInTz);
};

const getRequestActionTimestamps = (
  requestAction: RequestAction,
  scheduleReferenceDates: ScheduleReferenceDates,
  zone?: TimeZoneId,
): ActionTimestampWithValidity[] => {
  const { fixedAction, fixedTime, offset } = requestAction;
  if (fixedAction) {
    const fixedActionType = fixedAction.type;

    if (fixedActionType === FixedActionType.USER_CUSTOM_STATUS_SET) {
      return getStartActionTimestampsFromCustomStatus({
        fixedAction,
        customStatuses: scheduleReferenceDates.userCustomStatuses,
        zone,
        fixedTime,
        offset,
      });
    } else if (fixedActionType === FixedActionType.ORG_CUSTOM_STATUS_SET) {
      return getStartActionTimestampsFromCustomStatus({
        fixedAction,
        customStatuses: scheduleReferenceDates.orgCustomStatuses,
        zone,
        fixedTime,
        offset,
      });
    } else if (fixedActionType === FixedActionType.APPOINTMENT_BOOKED) {
      return getStartActionTimstampsFromVisits({
        fixedAction,
        appointmentReferenceDates: scheduleReferenceDates.visitReferenceDates,
        zone,
        fixedTime,
        offset,
      });
    } else {
      throw CspError.badState(`Unsupported actionCode: ${fixedActionType}`);
    }
  } else {
    return [];
  }
};

const getRequestActionTimestamp = (
  requestAction: RequestAction,
  scheduleReferenceDates: ScheduleReferenceDates,
  zone?: TimeZoneId,
): Maybe<ZonedDateTime> => {
  const actionDatesWithValidity = getRequestActionTimestamps(requestAction, scheduleReferenceDates, zone);
  return findLast(actionDatesWithValidity, actionDateValidity => actionDateValidity.isValid)?.actionTimestamp;
};

const getStartActionTimestamp = (
  { startAction }: GenericRequest,
  scheduleReferenceDates: ScheduleReferenceDates,
  zone?: TimeZoneId,
): Maybe<ZonedDateTime> => startAction && getRequestActionTimestamp(startAction, scheduleReferenceDates, zone);

const getEndActionTimestamp = (
  { endAction }: GenericRequest,
  scheduleReferenceDates: ScheduleReferenceDates,
  zone?: TimeZoneId,
): Maybe<ZonedDateTime> => endAction && getRequestActionTimestamp(endAction, scheduleReferenceDates, zone);

const getActionOffsetTimestamp = (
  requestAction: RequestAction,
  scheduleReferenceDates: ScheduleReferenceDates,
  timezoneId?: TimeZoneId,
): Maybe<ZonedDateTime> => {
  const startActionTimestamp = getRequestActionTimestamp(requestAction, scheduleReferenceDates);
  return startActionTimestamp && applyActionOffsetAndFixedTime(requestAction, startActionTimestamp, timezoneId);
};

const getStartActionOffsetTimestamp = (
  { startAction }: GenericRequest,
  scheduleReferenceDates: ScheduleReferenceDates,
  timezoneId?: TimeZoneId,
): Maybe<ZonedDateTime> => startAction && getActionOffsetTimestamp(startAction, scheduleReferenceDates, timezoneId);

const getEndActionOffsetTimestamp = (
  { endAction }: GenericRequest,
  scheduleReferenceDates: ScheduleReferenceDates,
  timezoneId?: TimeZoneId,
): Maybe<ZonedDateTime> => endAction && getActionOffsetTimestamp(endAction, scheduleReferenceDates, timezoneId);

export const RequestActionDateUtil = {
  getStartActionTimestamp,
  getEndActionTimestamp,
  getActionOffsetTimestamp,
  getStartActionOffsetTimestamp,
  getEndActionOffsetTimestamp,
  applyActionOffsetAndFixedTime,
  getRequestActionTimestamps,

  /** For testing purposes */
  getRequestActionTimestamp,
  getStartActionTimstampsFromVisits,
};
